import { createSlice } from '@reduxjs/toolkit';
import { Template } from '../../@types/campaign';
import {
  AssignableClaims,
  ChildTenant,
  ChildTenantAccessUser,
  Claim,
  NewOrganization,
  Organisation,
  OrganizationState,
  Sender
} from '../../@types/organization';
import axios, { axiosAmApiInstance as amApi } from '../../utils/axios';
import { dispatch } from '../store';
import { DispatchError, rejectWithError } from '../dispatchError';
import { buildUserAccessPayload } from '../../utils/userAccessPayload';
import { getDropzoneError } from 'components/CustomDropzone';
import { ReviewConfigurationDto } from '../../@types/reviews';
import { translator } from 'translation/useTranslator';
import { IvrMap } from '../../@types/ivr';

const initialState: OrganizationState = {
  loading: {},
  isLoading: false,
  isLogoLoading: false,
  organizationAnalytics: {
    campaignCount: 0,
    companyCount: 0,
    emailCount: 0,
    employeeCount: 0,
    orgId: '',
    respondentCount: 0,
    responseCount: 0,
    smsCount: 0,
    smsEnabled: false
  },
  organizationProfile: {
    id: '',
    displayName: '',
    organizationName: '',
    organizationPhone: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    invoiceEmail: '',
    cvr: '',
    agreementId: null,
    active: true,
    subscriptionType: 0,
    adminSeats: 0,
    userSeats: 0,
    outlookSeats: 0,
    emailLimit: null,
    smsEnabled: true,
    smsLimit: 0,
    logoLink: '',
    sid: '',
    reviewDescription: '',
    reviewName: '',
    created: null,
    website: '',
    enableHumanVerification: undefined,
    inactiveAt: null,
    isMfaForced: false,
    isPasswordLoginDisabled: false
  },
  languages: [],
  templates: [],
  currentTemplate: null,
  copyTemplate: null,
  logos: [],
  ivrMaps: [],
  ivrMap: null,
  senders: [],
  organizations: [],
  childTenants: [],
  childTenantAccessByTenantId: {},
  currentTenant: null,
  callDurationLimit: undefined,
  anonymizationConfiguration: undefined,
  assignableClaims: null
};

const slice = createSlice({
  name: 'organization',
  initialState,
  reducers: {
    setCallDurationLimit(state, action) {
      state.callDurationLimit = action.payload;
    },

    setAnonymizationConfiguration(state, action) {
      state.anonymizationConfiguration = action.payload;
    },

    // START LOADING
    startNewLoading(state, action) {
      state.loading[action.payload] = true;
    },

    // END LOADING
    endNewLoading(state, action) {
      state.loading[action.payload] = false;
    },

    startLoading(state) {
      state.isLoading = true;
    },

    // START LOGO LOADING
    startLogoLoading(state) {
      state.isLogoLoading = true;
    },

    // START LOGO LOADING
    stopLogoLoading(state) {
      state.isLogoLoading = false;
    },

    // GET ORGANIZATION ANALYTICS
    getOrganizationAnalyticsSuccess(state, action) {
      state.organizationAnalytics = action.payload;
      state.loading.organizationAnalytics = false;
    },

    // GET ORGANIZATION PROFILE
    getOrganizationProfileSuccess(state, action) {
      state.organizationProfile = action.payload;
      state.isLoading = false;
    },

    updateOrganizationProfileSuccess(state, action) {
      state.organizationProfile = action.payload;
      state.loading.organizationProfile = false;
    },

    //GET LANGUAGES
    getLanguagesSuccess(state, action) {
      state.languages = action.payload;
      state.isLoading = false;
    },

    // GET TEMPLATES
    getTemplatesSuccess(state, action) {
      state.templates = action.payload;
      state.isLoading = false;
    },

    // GET TEMPLATE
    getTemplateSuccess(state, action) {
      state.currentTemplate = action.payload;
      state.isLoading = false;
    },

    getLogosSuccess(state, action) {
      state.isLogoLoading = false;
      state.logos = action.payload;
    },

    addLogoSuccess(state) {
      state.isLogoLoading = false;
    },

    putTemplateSuccess(state, action) {
      state.currentTemplate = action.payload;
      state.isLoading = false;
    },

    changeTemplate(state, action) {
      state.isLoading = true;
      const templateId = action.payload;
      // number and any comparison
      // eslint-disable-next-line eqeqeq
      if (state.currentTemplate && templateId == state.currentTemplate.id) {
        state.isLoading = false;
        return;
      }

      // number and any comparison
      // eslint-disable-next-line eqeqeq
      let newTemplate = state.templates.find((template) => template.id == templateId);

      if (newTemplate) {
        state.currentTemplate = newTemplate;
      }

      state.isLoading = false;
    },

    getIvrMapsSuccess(state, action) {
      state.ivrMaps = action.payload;
    },

    getIvrMapSuccess(state, action) {
      state.ivrMap = action.payload;
    },

    getSendersSuccess(state, action) {
      state.loading.senders = false;
      state.senders = action.payload || [];
    },

    updateSenderSuccess(state) {
      state.loading.updatedSender = false;
    },

    deleteSenderSuccess(state) {
      state.loading.deleteSender = false;
    },

    getOrganizationsSuccess(state, action) {
      state.organizations = action.payload;
      state.loading.organizations = false;
    },

    getChildTenantsSuccess(state, action) {
      state.childTenants = action.payload;
      state.loading.childTenants = false;
    },

    getAssignableClaimsSuccess(state, action) {
      state.assignableClaims = action.payload;
      state.loading.assignableClaims = false;
    },

    getChildTenantAccessSuccess(state, action) {
      const { childTenantId, users } = action.payload;
      state.childTenantAccessByTenantId[childTenantId] = users;
      state.loading.childTenantAccess = false;
    },

    getCurrentTenantSuccess(state, action) {
      state.currentTenant = action.payload;
    },

    addSendersSuccess(state) {
      state.loading.addSender = false;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const { changeTemplate } = slice.actions;

export function getOrganizationAnalytics() {
  return async () => {
    dispatch(slice.actions.startNewLoading('organizationAnalytics'));
    try {
      const response = await axios.get('/analytics/organizationsummary');
      dispatch(slice.actions.endNewLoading('organizationAnalytics'));
      dispatch(slice.actions.getOrganizationAnalyticsSuccess(response.data));

      return await Promise.resolve({
        result: response,
        defaultSuccessMessage:
          translator?.organizationAnalyticsFetched() ?? 'Organization analytics fetched'
      });
    } catch (error) {
      dispatch(slice.actions.endNewLoading('organizationAnalytics'));

      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotFetchOrganizationAnalytics() ??
          'Could not fetch organization analytics'
      });
    }
  };
}

export function getOrganizationProfile() {
  return async () => {
    dispatch(slice.actions.startLoading());
    dispatch(slice.actions.startNewLoading('getOrganizationProfile'));
    try {
      const [orgResponse, licenseResponse, tenantResponse] = await Promise.all([
        axios.get('/organization'),
        axios.get('/license').catch(() => null),
        amApi.get<ChildTenant>('/tenants/current').catch(() => null)
      ]);

      const orgData = orgResponse.data;
      if (licenseResponse?.data?.customerVat) {
        orgData.cvr = licenseResponse.data.customerVat;
      }
      if (tenantResponse?.data) {
        orgData.isMfaForced = tenantResponse.data.isMfaForced;
        orgData.isPasswordLoginDisabled = tenantResponse.data.isPasswordLoginDisabled;
        orgData.isAiFeedbackEnabled = tenantResponse.data.isAiFeedbackEnabled;
      }

      dispatch(slice.actions.getCurrentTenantSuccess(tenantResponse?.data ?? null));
      dispatch(slice.actions.getOrganizationProfileSuccess(orgData));
      dispatch(slice.actions.endNewLoading('getOrganizationProfile'));
      return await Promise.resolve({
        result: orgResponse,
        defaultSuccessMessage:
          translator?.organizationProfileFetched() ?? 'Organization profile fetched'
      });
    } catch (error) {
      dispatch(slice.actions.endNewLoading('getOrganizationProfile'));
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotFetchOrganizationProfile() ?? 'Could not fetch Organization Profile'
      });
    }
  };
}

function convertEmptyStringsToNull<T extends object>(payload: T) {
  return Object.fromEntries(
    Object.entries(payload).map(([key, value]) => [key, value === '' ? null : value])
  );
}

export function updateCurrentTenant(tenant: ChildTenant) {
  dispatch(slice.actions.startNewLoading('tenant'));
  return async () => {
    const response = await amApi
      .put<ChildTenant>('/tenants/current', convertEmptyStringsToNull(tenant))
      .catch((error: any) => {
        dispatch(slice.actions.endNewLoading('tenant'));
        throw new DispatchError({
          error: error,
          defaultErrorMessage:
            translator?.couldNotUpdateOrganizationProfile() ??
            'Could not update organization profile'
        });
      });
    dispatch(slice.actions.endNewLoading('tenant'));
    return {
      result: response,
      defaultSuccessMessage:
        translator?.organizationProfileUpdated() ?? 'Organization profile updated'
    };
  };
}

export function updateEnableHumanVerification(enableHumanVerification: boolean) {
  dispatch(slice.actions.startNewLoading('enableHumanVerification'));
  return async () => {
    const response = await axios
      .put('/organization/human-verification', enableHumanVerification, {
        headers: { 'Content-Type': 'application/json' }
      })
      .catch((error: any) => {
        dispatch(slice.actions.endNewLoading('enableHumanVerification'));
        throw new DispatchError({
          error: error,
          defaultErrorMessage:
            translator?.couldNotUpdateOrganizationProfile() ??
            'Could not update organization profile'
        });
      });
    dispatch(slice.actions.updateOrganizationProfileSuccess(response.data));
    dispatch(slice.actions.endNewLoading('enableHumanVerification'));
    return {
      result: response,
      defaultSuccessMessage:
        translator?.organizationProfileUpdated() ?? 'Organization profile updated'
    };
  };
}

export function updateReviewConfiguration(dto: ReviewConfigurationDto) {
  dispatch(slice.actions.startNewLoading('reviewConfiguration'));
  return async () => {
    await axios.put('/reviews-configuration', dto).catch((error: any) => {
      dispatch(slice.actions.endNewLoading('reviewConfiguration'));
      throw new DispatchError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotUpdateReviewConfiguration() ?? 'Could not update review configuration'
      });
    });
    dispatch(slice.actions.endNewLoading('reviewConfiguration'));
    return {
      defaultSuccessMessage:
        translator?.reviewConfigurationUpdated() ?? 'Review configuration updated'
    };
  };
}

export function getLanguages() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/languages');
      dispatch(slice.actions.getLanguagesSuccess(response.data));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.languagesUpdated() ?? 'Languages updated'
      });
    } catch (error) {
      return await rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotFetchLanguages() ?? 'Could not fetch languages'
      });
    }
  };
}

export function getTemplates() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/templates');
      dispatch(slice.actions.getTemplatesSuccess(response.data));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.templatesUpdated() ?? 'Templates updated'
      });
    } catch (error) {
      return await rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotFetchTemplates() ?? 'Could not fetch templates'
      });
    }
  };
}

export function getLogos() {
  return async () => {
    dispatch(slice.actions.startLogoLoading());
    try {
      const response = await axios.get('/logos');
      dispatch(slice.actions.getLogosSuccess(response.data));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.logosUpdated() ?? 'Logos updated'
      });
    } catch (error) {
      dispatch(slice.actions.stopLogoLoading());

      return rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotFetchLogos() ?? 'Could not fetch logos'
      });
    }
  };
}

export function addLogo(file: any) {
  return async () => {
    if (file?.size > 3000000) {
      return rejectWithError({
        defaultErrorMessage: translator?.fileTooBig() ?? 'File too big'
      });
    }

    dispatch(slice.actions.startLogoLoading());
    try {
      let formData = new FormData();
      formData.append('image', file);
      await axios.post('/logos', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      dispatch(getLogos());
      dispatch(slice.actions.addLogoSuccess());
      return await Promise.resolve({
        defaultSuccessMessage: translator?.fileUploaded() ?? 'File uploaded'
      });
    } catch (error) {
      dispatch(slice.actions.stopLogoLoading());
      return await rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotUploadLogo() ?? 'Could not upload logo'
      });
    }
  };
}

export function updateOrgLogo(file: any) {
  return async () => {
    dispatch(slice.actions.startLogoLoading());
    try {
      let formData = new FormData();
      formData.append('logoImage', file);
      await amApi.post('/logos', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      await dispatch(getOrganizationProfile());
      dispatch(slice.actions.addLogoSuccess());
      return await Promise.resolve({
        defaultSuccessMessage: translator?.fileUploaded() ?? 'File uploaded'
      });
    } catch (error) {
      dispatch(slice.actions.stopLogoLoading());
      return await rejectWithError({
        error: error,
        defaultErrorMessage: getDropzoneError(
          translator?.couldNotUploadLogo() ?? 'Could not upload logo'
        )
      });
    }
  };
}

export function deleteLogo(fileName: string) {
  return async () => {
    dispatch(slice.actions.startLogoLoading());
    try {
      const params = {
        filename: fileName
      };
      await axios.delete('/logos', { params });
      await dispatch(getLogos());
      return await Promise.resolve({
        defaultSuccessMessage: translator?.logoDeleted() ?? `Logo deleted`
      });
    } catch (error) {
      dispatch(slice.actions.stopLogoLoading());
      const campaignIdsThatUseLogo = error as [];
      const isPlural = campaignIdsThatUseLogo?.length > 1;
      const ending = isPlural ? 's' : '';
      const campaignCountString = `${campaignIdsThatUseLogo?.length}`;
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotDeleteLogoAsItsUsedInTheFollowingCampaign(
            campaignCountString,
            campaignIdsThatUseLogo.join(', ')
          ) ??
          `Could not delete logo as it is used in following campaign${ending} with id${ending}: ${campaignIdsThatUseLogo.join(
            ', '
          )}`
      });
    }
  };
}

export function getTemplate(id: number) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/templates/' + id);
      dispatch(slice.actions.getTemplateSuccess(response.data));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage:
          translator?.templateFetchedWithId(`${id}`) ?? `Template with ID ${id} fetched`
      });
    } catch (error) {
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotFetchTemplateWithId(`${id}`) ??
          `Could not fetch template with ID ${id}`
      });
    }
  };
}

//'Created new template ' + values.template.name
export function postTemplate(template: Template) {
  return async () => {
    dispatch(slice.actions.startNewLoading('template'));
    await axios.post('/templates', template).catch((error: any) => {
      dispatch(slice.actions.endNewLoading('template'));
      return rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotCreateTemplate() ?? 'Template could not be created'
      });
    });
    dispatch(slice.actions.endNewLoading('template'));
    return {
      defaultSuccessMessage:
        translator?.templateCreatedWithName(template.name) ??
        `Template created with name ${template.name}`
    };
  };
}

export function putTemplate(template: Template) {
  return async () => {
    dispatch(slice.actions.startNewLoading('template'));
    const response = await axios.put('/templates/' + template.id, template).catch((error: any) => {
      dispatch(slice.actions.endNewLoading('template'));
      return rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotUpdateTemplate() ?? 'Could not update template'
      });
    });
    dispatch(slice.actions.putTemplateSuccess(response?.data));
    dispatch(slice.actions.endNewLoading('template'));
    return {
      result: response,
      defaultSuccessMessage: translator?.templateUpdated() ?? `Template updated`
    };
  };
}

type TestWebhookProps = {
  url: string;
  webHookAuthenticationMethodId?: string;
};

export function testWebhook({ url, webHookAuthenticationMethodId }: TestWebhookProps) {
  dispatch(slice.actions.startNewLoading('webhook'));
  return async () => {
    const headers = {
      'Content-Type': 'application/json; charset=utf-8'
    };
    const response = await axios
      .post(
        '/webhooks/testwebhook',
        { url, webHookAuthenticationMethodId },
        {
          headers: headers
        }
      )
      .catch((error: any) => {
        dispatch(slice.actions.endNewLoading('webhook'));
        return rejectWithError({
          error: error,
          defaultErrorMessage: translator?.errorTestingWebhookCall() ?? 'Error testing webhook call'
        });
      });
    dispatch(slice.actions.endNewLoading('webhook'));
    if (response.data) {
      return {
        result: response,
        defaultSuccessMessage: translator?.webhookCallSucceeded() ?? 'Webhook call succeeded'
      };
    } else {
      return rejectWithError({
        defaultErrorMessage: translator?.webhookCallFailed() ?? 'Webhook call failed'
      });
    }
  };
}

export function getIvrMaps() {
  dispatch(slice.actions.startNewLoading('ivr'));
  return async () => {
    try {
      const response = await axios.get('/ivr/maps');
      dispatch(slice.actions.getIvrMapsSuccess(response.data));
      dispatch(slice.actions.endNewLoading('ivr'));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.ivrMapsFetched() ?? 'IVR maps fetched'
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading('ivr'));
      return await rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotFetchIvrMaps() ?? `Could not fetch IVR maps`
      });
    }
  };
}

export function getIvrMap(id: number | null) {
  return async () => {
    if (!id) {
      dispatch(slice.actions.getIvrMapSuccess(initialState.ivrMap));
      return {
        defaultSuccessMessage: translator?.noIvrMapToFetch() ?? 'No IVR map to fetch'
      };
    }
    dispatch(slice.actions.startNewLoading('ivrMap'));

    try {
      const response = await axios.get('/ivr/maps/' + id);
      dispatch(slice.actions.getIvrMapSuccess(response.data));
      dispatch(slice.actions.endNewLoading('ivrMap'));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.ivrMapFetched() ?? 'IVR map fetched'
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading('ivrMap'));
      return await rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotFetchIvrMap() ?? `Could not fetch IVR map`
      });
    }
  };
}

export function addIvrMap(ivrMap: Partial<IvrMap>) {
  dispatch(slice.actions.startNewLoading('ivr'));
  return async () => {
    try {
      await axios.post('/ivr/maps', ivrMap);
      dispatch(slice.actions.endNewLoading('ivr'));
      return await Promise.resolve({
        defaultSuccessMessage: translator?.ivrMapAdded() ?? 'IVR map added'
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading('ivr'));
      return await rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotAddIvrMap() ?? 'Could not add IVR map'
      });
    }
  };
}

export function updateIvrMap(ivrMap: Partial<IvrMap>) {
  dispatch(slice.actions.startNewLoading('ivr'));
  return async () => {
    try {
      await axios.put('/ivr/maps/' + ivrMap.id, ivrMap);
      dispatch(slice.actions.endNewLoading('ivr'));
      return await Promise.resolve({
        defaultSuccessMessage: translator?.ivrMapUpdated() ?? 'IVR map updated'
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading('ivr'));
      return await rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotUpdateIvrMap() ?? 'Could not update IVR map'
      });
    }
  };
}

export function deleteIvrMaps(ids: number[]) {
  dispatch(slice.actions.startNewLoading('ivr'));
  return async () => {
    try {
      await Promise.all(ids.map((id) => axios.delete('/ivr/maps/' + id)));
      dispatch(slice.actions.endNewLoading('ivr'));
      return await Promise.resolve({
        defaultSuccessMessage: translator?.ivrMapDeleted() ?? 'IVR map deleted'
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading('ivr'));
      return await rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotDeleteIvrMap() ?? 'Could not delete IVR map'
      });
    }
  };
}

export function getSenders() {
  dispatch(slice.actions.startNewLoading('senders'));
  return async () => {
    try {
      const response = await axios.get('/organization/senders');
      dispatch(slice.actions.getSendersSuccess(response.data));
      dispatch(slice.actions.endNewLoading('senders'));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.sendersFetched() ?? 'Senders fetched'
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading('senders'));
      return rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotFetchSenders() ?? 'Could not fetch senders'
      });
    }
  };
}

export function addSenders(senders: Sender[]) {
  dispatch(slice.actions.startNewLoading('addSenders'));
  return async () => {
    try {
      await axios.post('/organization/senders', senders);
      dispatch(slice.actions.addSendersSuccess());
      dispatch(slice.actions.endNewLoading('addSenders'));
      return await Promise.resolve({
        defaultSuccessMessage:
          translator?.sendersAdded(`${senders.length}`) ??
          `Sender${senders.length > 1 ? 's' : ''} added`
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading('addSenders'));
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotAddSenders(`${senders.length}`) ??
          `Could not add sender${senders.length > 1 ? 's' : ''}`
      });
    }
  };
}

export function updateSender(updatedSender: Sender) {
  dispatch(slice.actions.startNewLoading('updateSender'));
  return async () => {
    try {
      await axios.put(`/organization/senders/${updatedSender.id}`, updatedSender);
      dispatch(slice.actions.updateSenderSuccess());
      dispatch(slice.actions.endNewLoading('updateSender'));
      return await Promise.resolve({
        defaultSuccessMessage: translator?.senderUpdated() ?? 'Sender updated'
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading('updateSender'));
      return await rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotUpdateSender() ?? 'Could not update sender'
      });
    }
  };
}

export function deleteSender(senderId: number) {
  dispatch(slice.actions.startNewLoading('deleteSender'));
  return async () => {
    try {
      await axios.delete(`/organization/senders/${senderId}`);
      dispatch(slice.actions.deleteSenderSuccess());
      dispatch(slice.actions.endNewLoading('deleteSender'));
      return await Promise.resolve({
        defaultSuccessMessage: translator?.senderDeleted() ?? 'Sender deleted'
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading('deleteSender'));
      return await rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotDeleteSender() ?? 'Could not delete sender'
      });
    }
  };
}

export function getOrganizations() {
  return async () => {
    dispatch(slice.actions.startNewLoading('organizations'));
    try {
      const response = await amApi.get('tenants/access');

      dispatch(
        slice.actions.getOrganizationsSuccess(
          response.data?.accessibleTenants.map(
            (tenant: any): Organisation => ({
              organizationId: tenant.id,
              organizationName: tenant.name,
              displayName: tenant.displayName,
              active: true
            })
          ) || []
        )
      );
      dispatch(slice.actions.endNewLoading('organizations'));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.organizationsFetched() ?? 'Organizations fetched'
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading('organizations'));
      return rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotFetchOrganizations() ?? 'Could not fetch organizations'
      });
    }
  };
}

export function getChildTenants() {
  return async () => {
    dispatch(slice.actions.startNewLoading('childTenants'));
    try {
      const response = await amApi.get('/child-tenants');
      const children: ChildTenant[] = response.data?.children ?? [];
      dispatch(slice.actions.getChildTenantsSuccess(children));
      dispatch(slice.actions.endNewLoading('childTenants'));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.organizationsFetched() ?? 'Organizations fetched'
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading('childTenants'));
      throw new DispatchError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotFetchOrganizations() ?? 'Could not fetch organizations'
      });
    }
  };
}

export function getAssignableClaims(childTenantId?: string) {
  return async () => {
    dispatch(slice.actions.startNewLoading('assignableClaims'));
    try {
      const url = childTenantId
        ? `/child-tenants/${childTenantId}/claims`
        : '/tenants/current/claims';
      const response = await amApi.get(url);
      const assignableClaims: AssignableClaims = {
        claims: response.data?.claims ?? [],
        managementClaims: response.data?.managementClaims ?? [],
        featureClaims: response.data?.featureClaims ?? []
      };
      dispatch(slice.actions.getAssignableClaimsSuccess(assignableClaims));
      dispatch(slice.actions.endNewLoading('assignableClaims'));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage:
          translator?.assignableClaimsFetched() ?? 'Assignable permissions fetched'
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading('assignableClaims'));
      return rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotFetchAssignableClaims() ?? 'Could not fetch assignable permissions'
      });
    }
  };
}

export type ChildTenantAccessProps = {
  childTenant: ChildTenant;
  userId: string;
};

export function getChildTenantAccess({ childTenant }: { childTenant: ChildTenant }) {
  return async () => {
    dispatch(slice.actions.startNewLoading('childTenantAccess'));
    try {
      const response = await amApi.get(`/child-tenants/${childTenant.id}/access`);

      dispatch(
        slice.actions.getChildTenantAccessSuccess({
          childTenantId: childTenant.id,
          users: (response.data?.users ?? []).map(
            (user: any): ChildTenantAccessUser => ({
              userId: user.userId,
              email: user.email,
              name: user.name,
              isEmailConfirmed: user.isEmailConfirmed ?? false,
              claims: (user.claims ?? []).map((claim: any) => ({
                claimType: claim.type ?? claim.claimType,
                claimValue: claim.value ?? claim.claimValue
              }))
            })
          )
        })
      );
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.childTenantAccessFetched() ?? 'Access fetched'
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading('childTenantAccess'));
      return rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotFetchChildTenantAccess() ?? 'Could not fetch access'
      });
    }
  };
}

export type GrantChildTenantAccessProps = ChildTenantAccessProps & {
  role: string;
  claims: Claim[];
};

export function grantChildTenantAccess({
  childTenant,
  userId,
  role,
  claims
}: GrantChildTenantAccessProps) {
  return async () => {
    dispatch(slice.actions.startNewLoading('grantChildTenantAccess'));
    try {
      const response = await amApi.post(
        `/child-tenants/${childTenant.id}/access/${userId}`,
        buildUserAccessPayload(role, claims)
      );

      dispatch(slice.actions.endNewLoading('grantChildTenantAccess'));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.childTenantAccessGranted() ?? 'Access granted'
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading('grantChildTenantAccess'));
      return rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotGrantChildTenantAccess() ?? 'Could not grant access'
      });
    }
  };
}

export function removeChildTenantAccess({ childTenant, userId }: ChildTenantAccessProps) {
  return async () => {
    dispatch(slice.actions.startNewLoading('childTenantAccess'));
    try {
      const response = await amApi.delete(`/child-tenants/${childTenant.id}/access/${userId}`);

      dispatch(slice.actions.endNewLoading('childTenantAccess'));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.childTenantAccessRemoved() ?? 'Access removed'
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading('childTenantAccess'));
      return rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotRemoveChildTenantAccess() ?? 'Could not remove access'
      });
    }
  };
}

export function addOrganization(newOrg: NewOrganization) {
  return async () => {
    dispatch(slice.actions.startNewLoading('addOrganization'));
    try {
      const accessPayload = buildUserAccessPayload(newOrg.role, newOrg.claims);
      const tenantPayload = Object.fromEntries(
        Object.entries({
          name: newOrg.organizationName,
          displayName: newOrg.displayName,
          phoneNumber: newOrg.organizationPhone,
          invoiceEmail: newOrg.invoiceEmail,
          website: newOrg.website,
          vat: newOrg.vat,
          userEmail: newOrg.contactEmail,
          role: accessPayload.role,
          claims: accessPayload.claims,
          managementClaims: accessPayload.managementClaims,
          featureClaims: accessPayload.featureClaims
        }).filter(([, v]) => v != null && v !== '')
      );
      const tenantResponse = await amApi.post('/child-tenants', tenantPayload);
      const tenantId = tenantResponse.data?.id;

      if (!tenantId) {
        throw new Error('Tenant creation failed to return an ID');
      }

      dispatch(slice.actions.endNewLoading('addOrganization'));
      return await Promise.resolve({
        result: tenantResponse,
        defaultSuccessMessage: translator?.organizationAdded() ?? 'Organization added'
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading('addOrganization'));
      return rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotAddOrganization() ?? 'Could not add organization'
      });
    }
  };
}

export function getCallDurationLimit() {
  return async () => {
    dispatch(slice.actions.startNewLoading('getCallDurationLimit'));
    try {
      const response = await axios.get(`/organization/calldurations`);

      dispatch(slice.actions.setCallDurationLimit(response.data));
      dispatch(slice.actions.endNewLoading('getCallDurationLimit'));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage:
          translator?.minimumCallDurationFetched() ?? 'Minimum call duration fetched'
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading('getCallDurationLimit'));
      return rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotFetchMinimumCallDuration() ?? 'Could not fetch minimum call duration'
      });
    }
  };
}

type SetCallDurationLimitProps = {
  seconds: number;
};
export function setCallDurationLimit({ seconds }: SetCallDurationLimitProps) {
  return async () => {
    dispatch(slice.actions.startNewLoading('setCallDurationLimit'));
    try {
      const response = await axios.put(`/organization/calldurations?seconds=${seconds}`);
      dispatch(slice.actions.endNewLoading('setCallDurationLimit'));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage:
          seconds > 0
            ? translator?.minimumCallDurationUpdated() ?? 'Minimum call duration updated'
            : translator?.minimumCallDurationDisabled() ?? 'Minimum call duration disabled'
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading('setCallDurationLimit'));
      return rejectWithError({
        error: error,
        defaultErrorMessage:
          seconds > 0
            ? translator?.couldNotUpdateMinimumCallDuration() ??
              'Could not update minimum call duration'
            : translator?.couldNotDisableMinimumCallDuration() ??
              'Could not disable minimum call duration'
      });
    }
  };
}

export function getAutomaticAnonymization() {
  return async () => {
    dispatch(slice.actions.startNewLoading('getAutomaticAnonymization'));
    try {
      const response = await axios.get(`/organization/anonymization`);

      dispatch(slice.actions.setAnonymizationConfiguration(response.data));
      dispatch(slice.actions.endNewLoading('getAutomaticAnonymization'));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage:
          translator?.automaticAnonymizationFetched() ?? 'Automatic anonymization fetched'
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading('getAutomaticAnonymization'));
      return rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotFetchAutomaticAnonymization() ??
          'Could not fetch automatic anonymization'
      });
    }
  };
}

type SetAutomaticAnonymizationProps = {
  anonymizeAfterDays?: number | null;
  anonymizeEmployees: boolean;
  anonymizeCompanies: boolean;
  anonymizeUsers: boolean;
};
export function setAutomaticAnonymization({
  anonymizeAfterDays,
  anonymizeCompanies,
  anonymizeEmployees,
  anonymizeUsers
}: SetAutomaticAnonymizationProps) {
  return async () => {
    dispatch(slice.actions.startNewLoading('setAutomaticAnonymization'));
    try {
      const response = await axios.put(`/organization/anonymization`, {
        anonymizeAfterDays,
        anonymizeCompanies,
        anonymizeEmployees,
        anonymizeUsers
      });
      dispatch(slice.actions.endNewLoading('setAutomaticAnonymization'));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: anonymizeAfterDays
          ? translator?.automaticAnonymizationUpdated() ?? 'Automatic anonymization updated'
          : translator?.automaticAnonymizationDisabled() ?? 'Automatic anonymization disabled'
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading('setAutomaticAnonymization'));
      return rejectWithError({
        error: error,
        defaultErrorMessage: anonymizeAfterDays
          ? translator?.couldNotUpdateAutomaticAnonymization() ??
            'Could not update automatic anonymization'
          : translator?.couldNotDisableAutomaticAnonymization() ??
            'Could not disable automatic anonymization'
      });
    }
  };
}

type SetActivityLogAnonymizationProps = {
  activityLogAnonymizeAfterDays: number;
  anonymizeAfterDays?: number | null;
  anonymizeEmployees: boolean;
  anonymizeCompanies: boolean;
  anonymizeUsers: boolean;
};
export function setActivityLogAnonymization({
  activityLogAnonymizeAfterDays,
  anonymizeAfterDays,
  anonymizeCompanies,
  anonymizeEmployees,
  anonymizeUsers
}: SetActivityLogAnonymizationProps) {
  return async () => {
    dispatch(slice.actions.startNewLoading('setActivityLogAnonymization'));
    try {
      const response = await axios.put(`/organization/anonymization`, {
        activityLogAnonymizeAfterDays,
        anonymizeAfterDays,
        anonymizeCompanies,
        anonymizeEmployees,
        anonymizeUsers
      });
      dispatch(slice.actions.endNewLoading('setActivityLogAnonymization'));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage:
          translator?.activityLogAnonymizationUpdated() ?? 'Activity log anonymization updated'
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading('setActivityLogAnonymization'));
      return rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotUpdateActivityLogAnonymization() ??
          'Could not update activity log anonymization'
      });
    }
  };
}

type UpdateTenantDeactivationDateProps = {
  tenantId: string;
  tenantName: string;
  displayName?: string | null;
  phoneNumber?: string | null;
  invoiceEmail?: string | null;
  vat: string | null;
  website?: string | null;
  parentTenantId?: string | null;
  deactivationReminderEmail: string | null;
  active: boolean;
  isMfaForced?: boolean;
  isPasswordLoginDisabled?: boolean;
  isAiFeedbackEnabled?: boolean;
  deactivateAt: Date | null;
};
export function updateTenantDeactivationDate({
  tenantId,
  tenantName,
  displayName,
  phoneNumber,
  invoiceEmail,
  vat,
  website,
  parentTenantId,
  deactivationReminderEmail,
  active,
  isMfaForced,
  isPasswordLoginDisabled,
  isAiFeedbackEnabled,
  deactivateAt
}: UpdateTenantDeactivationDateProps) {
  return async () => {
    dispatch(slice.actions.startNewLoading('updateTenantDeactivationDate'));
    try {
      const updatePayload = {
        name: tenantName,
        displayName: displayName || null,
        phoneNumber: phoneNumber || null,
        invoiceEmail: invoiceEmail || null,
        vat: vat || null,
        website: website || null,
        isMfaForced: isMfaForced ?? false,
        isPasswordLoginDisabled: isPasswordLoginDisabled ?? false,
        isAiFeedbackEnabled: isAiFeedbackEnabled ?? false,
        parentTenantId: parentTenantId || null,
        deactivationReminderEmail: deactivationReminderEmail || null,
        deactivateAt: deactivateAt ? deactivateAt.toISOString() : null,
        active
      };

      const response = await amApi.put(`/tenants/${tenantId}`, updatePayload);

      dispatch(slice.actions.endNewLoading('updateTenantDeactivationDate'));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage:
          translator?.organizationDeactivationDateUpdated() ??
          'Organization deactivation date updated'
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading('updateTenantDeactivationDate'));
      throw new DispatchError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotUpdateOrganizationDeactivationDate() ??
          'Could not update organization deactivation date'
      });
    }
  };
}

export function getCurrentTenant() {
  return async () => {
    dispatch(slice.actions.startNewLoading('getCurrentTenant'));
    try {
      const response = await amApi.get<ChildTenant>(`/tenants/current`);

      dispatch(slice.actions.getCurrentTenantSuccess(response.data ?? null));
      dispatch(slice.actions.endNewLoading('getCurrentTenant'));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage:
          translator?.organizationProfileFetched() ?? 'Organization profile fetched'
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading('getCurrentTenant'));
      throw new DispatchError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotFetchOrganizationProfile() ?? 'Could not fetch organization profile'
      });
    }
  };
}

type ReactivateOrganizationProps = {
  tenantId: string;
  tenantName: string;
  displayName?: string | null;
  phoneNumber?: string | null;
  invoiceEmail?: string | null;
  vat: string | null;
  website?: string | null;
  parentTenantId?: string | null;
  deactivationReminderEmail: string | null;
  isMfaForced?: boolean;
  isPasswordLoginDisabled?: boolean;
  isAiFeedbackEnabled?: boolean;
};
export function reactivateOrganization({
  tenantId,
  tenantName,
  displayName,
  phoneNumber,
  invoiceEmail,
  vat,
  website,
  parentTenantId,
  deactivationReminderEmail,
  isMfaForced,
  isPasswordLoginDisabled,
  isAiFeedbackEnabled
}: ReactivateOrganizationProps) {
  return async () => {
    dispatch(slice.actions.startNewLoading('reactivateOrganization'));
    try {
      const updatePayload = {
        name: tenantName,
        displayName: displayName || null,
        phoneNumber: phoneNumber || null,
        invoiceEmail: invoiceEmail || null,
        vat: vat || null,
        website: website || null,
        isMfaForced: isMfaForced ?? false,
        isPasswordLoginDisabled: isPasswordLoginDisabled ?? false,
        isAiFeedbackEnabled: isAiFeedbackEnabled ?? false,
        parentTenantId: parentTenantId || null,
        deactivationReminderEmail: deactivationReminderEmail || null,
        deactivateAt: null,
        active: true
      };

      const response = await amApi.put(`/tenants/${tenantId}`, updatePayload);
      dispatch(slice.actions.endNewLoading('reactivateOrganization'));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.organizationReactivated() ?? 'Organization reactivated'
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading('reactivateOrganization'));
      return rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotReactivateOrganization() ?? 'Could not reactivate organization'
      });
    }
  };
}

export function updateSmsEnabled({
  organizationId,
  smsEnabled
}: {
  organizationId: string;
  smsEnabled: boolean;
}) {
  return async () => {
    dispatch(slice.actions.startNewLoading('updateSmsEnabled'));
    try {
      const response = await axios.put(`/organization/${organizationId}/sms`, smsEnabled, {
        headers: { 'Content-Type': 'application/json' }
      });
      dispatch(slice.actions.endNewLoading('updateSmsEnabled'));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.smsEnabled() ?? 'SMS enabled'
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading('updateSmsEnabled'));
      throw new DispatchError({
        error: error,
        defaultErrorMessage: translator?.smsEnabledError() ?? 'Could not enable SMS'
      });
    }
  };
}

type UpdateAiFeedbackEnabledProps = {
  tenantId: string;
  tenantName: string;
  displayName?: string | null;
  phoneNumber?: string | null;
  invoiceEmail?: string | null;
  vat: string | null;
  website?: string | null;
  parentTenantId?: string | null;
  deactivationReminderEmail: string | null;
  active: boolean;
  isMfaForced?: boolean;
  isPasswordLoginDisabled?: boolean;
  deactivateAt: string | null;
  isAiFeedbackEnabled: boolean;
};
export function updateAiFeedbackEnabled({
  tenantId,
  tenantName,
  displayName,
  phoneNumber,
  invoiceEmail,
  vat,
  website,
  parentTenantId,
  deactivationReminderEmail,
  active,
  isMfaForced,
  isPasswordLoginDisabled,
  deactivateAt,
  isAiFeedbackEnabled
}: UpdateAiFeedbackEnabledProps) {
  return async () => {
    dispatch(slice.actions.startNewLoading('updateAiFeedbackEnabled'));
    try {
      const updatePayload = {
        name: tenantName,
        displayName: displayName || null,
        phoneNumber: phoneNumber || null,
        invoiceEmail: invoiceEmail || null,
        vat: vat || null,
        website: website || null,
        isMfaForced: isMfaForced ?? false,
        isPasswordLoginDisabled: isPasswordLoginDisabled ?? false,
        parentTenantId: parentTenantId || null,
        deactivationReminderEmail: deactivationReminderEmail || null,
        deactivateAt: deactivateAt || null,
        active,
        isAiFeedbackEnabled
      };

      const response = await amApi.put(`/tenants/${tenantId}`, updatePayload);
      dispatch(slice.actions.endNewLoading('updateAiFeedbackEnabled'));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage:
          translator?.aiFeedbackEnabledUpdated() ?? 'AI feedback setting updated'
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading('updateAiFeedbackEnabled'));
      return rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.aiFeedbackEnabledError() ?? 'Could not update AI feedback setting'
      });
    }
  };
}
